/**
 * Offline training-dummy opponent for Tutorial mode.
 * Speaks PeerMessage on a loopback PeerLink as the guest.
 */

import {
  AIM_TIMEOUT_MS,
  AIM_TURN_SPEED,
  GUEST_SPAWN,
  MANEUVER_LERP_SPEED,
  MANEUVER_STEP,
  MAX_SPREAD_HALF_RAD,
  STATE_SYNC_MS,
  clampToOwnHalf,
  type PeerMessage,
} from "@duelgame/shared";
import { randomEmoteLine } from "./emotes";
import type { PeerLink } from "../net/peerLink";

type BotPhase = "waiting" | "settle" | "maneuver" | "aim" | "done";

const AIM_TURN_SCALE = 0.2;
/** Fire once the aim window is this far through. */
const SHOOT_AT_FRAC = 0.35;
/** Send a T-taunt this far into the pace-off countdown. */
const TAUNT_AT_MS = 6000;

function angleDiff(from: number, to: number): number {
  let diff = to - from;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return diff;
}

function turnToward(current: number, target: number, maxDelta: number): number {
  const diff = angleDiff(current, target);
  if (Math.abs(diff) <= maxDelta) return target;
  return current + Math.sign(diff) * maxDelta;
}

export class TutorialBot {
  private link: PeerLink;
  private unsub: (() => void) | null = null;
  private phase: BotPhase = "waiting";
  private settleEndMs: number | null = null;
  private maneuverEndMs: number | null = null;
  private aimEndMs: number | null = null;
  private lastTickShown: number | null = null;
  private lastSyncMs = 0;
  private hasShot = false;
  private hasTaunted = false;
  private hit = false;

  private x = GUEST_SPAWN.x;
  private y = GUEST_SPAWN.y;
  private facing = GUEST_SPAWN.facing;
  private facingLeft = Math.cos(GUEST_SPAWN.facing) < 0;
  private spreadHalf = 0;
  private stepCount = 0;
  private stepProgress = 0;
  private stepDist = 0;
  private moveToX: number | null = null;
  private moveToY: number | null = null;

  private playerX = 0;
  private playerY = 0;

  constructor(link: PeerLink) {
    this.link = link;
    this.unsub = link.onPeerMessage((msg) => this.onPeer(msg));
  }

  destroy(): void {
    this.unsub?.();
    this.unsub = null;
  }

  /** Keep bot phase timers aligned with a paused host clock. */
  shiftClocks(deltaMs: number): void {
    if (deltaMs <= 0) return;
    if (this.settleEndMs !== null) this.settleEndMs += deltaMs;
    if (this.maneuverEndMs !== null) this.maneuverEndMs += deltaMs;
    if (this.aimEndMs !== null) this.aimEndMs += deltaMs;
    this.lastSyncMs += deltaMs;
  }

  /** Reset pose for another tutorial round (keeps the same PeerLink). */
  reset(): void {
    this.phase = "waiting";
    this.settleEndMs = null;
    this.maneuverEndMs = null;
    this.aimEndMs = null;
    this.lastTickShown = null;
    this.lastSyncMs = 0;
    this.hasShot = false;
    this.hasTaunted = false;
    this.hit = false;
    this.x = GUEST_SPAWN.x;
    this.y = GUEST_SPAWN.y;
    this.facing = GUEST_SPAWN.facing;
    this.facingLeft = Math.cos(GUEST_SPAWN.facing) < 0;
    this.spreadHalf = 0;
    this.stepCount = 0;
    this.stepProgress = 0;
    this.stepDist = 0;
    this.moveToX = null;
    this.moveToY = null;
  }

  tick(now: number, dtMs: number): void {
    if (this.phase === "waiting" || this.phase === "done") return;
    const dt = dtMs / 1000;

    if (
      this.phase === "settle" &&
      this.settleEndMs !== null &&
      now >= this.settleEndMs
    ) {
      this.phase = "maneuver";
      this.facing = 0;
      this.facingLeft = false;
      this.lastTickShown = null;
    }

    if (this.phase === "maneuver" && this.maneuverEndMs !== null) {
      this.updateManeuver(now, dt);
      this.maybeTaunt(now);
      if (now >= this.maneuverEndMs) {
        this.phase = "aim";
        this.moveToX = null;
        this.moveToY = null;
        this.stepProgress = 0;
        this.stepDist = 0;
        this.spreadHalf = MAX_SPREAD_HALF_RAD;
      }
    }

    if (this.phase === "aim" && this.aimEndMs !== null) {
      this.updateAim(now, dt);
      if (now >= this.aimEndMs) {
        this.phase = "done";
      }
    }

    if (now - this.lastSyncMs >= STATE_SYNC_MS) {
      this.lastSyncMs = now;
      this.sendState(now);
    }
  }

  private onPeer(msg: PeerMessage): void {
    switch (msg.type) {
      case "ready":
        if (msg.ready) {
          this.link.sendPeer({ type: "ready", ready: true, abilities: [] });
        }
        break;
      case "match_start": {
        const origin = performance.now();
        this.settleEndMs = origin + msg.settleMs;
        this.maneuverEndMs = origin + msg.settleMs + msg.maneuverMs;
        this.aimEndMs = origin + msg.settleMs + msg.maneuverMs + msg.aimMs;
        this.phase = "settle";
        this.hasShot = false;
        this.hasTaunted = false;
        this.hit = false;
        this.x = GUEST_SPAWN.x;
        this.y = GUEST_SPAWN.y;
        this.facing = GUEST_SPAWN.facing;
        this.facingLeft = Math.cos(GUEST_SPAWN.facing) < 0;
        this.spreadHalf = 0;
        this.stepCount = 0;
        this.stepProgress = 0;
        this.moveToX = null;
        this.moveToY = null;
        this.lastTickShown = null;
        break;
      }
      case "state":
        this.playerX = msg.x;
        this.playerY = msg.y;
        break;
      case "hit":
        if (msg.role === "guest") this.hit = true;
        break;
      case "result":
        this.phase = "done";
        break;
      case "rematch":
        this.reset();
        this.link.sendPeer({ type: "rematch" });
        break;
      default:
        break;
    }
  }

  private updateManeuver(now: number, dt: number): void {
    if (this.maneuverEndMs === null) return;
    const remaining = Math.ceil((this.maneuverEndMs - now) / 1000);
    const display = Math.max(0, remaining);

    if (display !== this.lastTickShown) {
      if (this.lastTickShown !== null && display < this.lastTickShown) {
        this.queueRandomPace();
      }
      this.lastTickShown = display;
    }

    if (this.moveToX !== null && this.moveToY !== null) {
      const dx = this.moveToX - this.x;
      const dy = this.moveToY - this.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 1) {
        this.x = this.moveToX;
        this.y = this.moveToY;
        this.moveToX = null;
        this.moveToY = null;
        this.stepProgress = 0;
        this.stepDist = 0;
      } else {
        const step = MANEUVER_LERP_SPEED * dt;
        const t = Math.min(1, step / dist);
        this.x += dx * t;
        this.y += dy * t;
        this.facing = Math.atan2(dy, dx);
        this.facingLeft = dx < 0;
        if (this.stepDist > 0) {
          this.stepProgress = Math.min(1, 1 - dist / this.stepDist);
        }
      }
    }
  }

  private maybeTaunt(now: number): void {
    if (this.hasTaunted || this.settleEndMs === null) return;
    if (now - this.settleEndMs < TAUNT_AT_MS) return;
    this.hasTaunted = true;
    this.link.sendPeer({ type: "emote", text: randomEmoteLine() });
  }

  private queueRandomPace(): void {
    if (this.moveToX !== null) return;
    // Always pace rightward, with a bit of vertical wander.
    const angle = (Math.random() - 0.5) * (Math.PI / 2);
    const next = clampToOwnHalf(
      "guest",
      this.x + Math.cos(angle) * MANEUVER_STEP,
      this.y + Math.sin(angle) * MANEUVER_STEP
    );
    const dx = next.x - this.x;
    const dy = next.y - this.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 1) return;
    this.moveToX = next.x;
    this.moveToY = next.y;
    this.stepDist = dist;
    this.stepProgress = 0;
    this.stepCount += 1;
    this.facing = Math.atan2(dy, dx);
    this.facingLeft = dx < 0;
  }

  private updateAim(now: number, dt: number): void {
    if (this.hasShot || this.hit || this.aimEndMs === null) return;

    const target = Math.atan2(this.playerY - this.y, this.playerX - this.x);
    this.facing = turnToward(
      this.facing,
      target,
      AIM_TURN_SPEED * AIM_TURN_SCALE * dt
    );
    this.facingLeft = Math.cos(this.facing) < 0;
    // Stay loose — training dummy never tightens the cone.
    this.spreadHalf = MAX_SPREAD_HALF_RAD;

    const aimStart = this.aimEndMs - AIM_TIMEOUT_MS;
    const elapsed = now - aimStart;
    if (elapsed >= AIM_TIMEOUT_MS * SHOOT_AT_FRAC) {
      this.fire();
    }
  }

  private fire(): void {
    if (this.hasShot || this.hit) return;
    this.hasShot = true;
    const fireFacing =
      this.facing + (Math.random() * 2 - 1) * this.spreadHalf;
    this.link.sendPeer({
      type: "shoot",
      x: this.x,
      y: this.y,
      facing: fireFacing,
    });
  }

  private sendState(now: number): void {
    this.link.sendPeer({
      type: "state",
      x: this.x,
      y: this.y,
      facing: this.facing,
      spreadHalf: this.spreadHalf,
      facingLeft: this.facingLeft,
      stepCount: this.stepCount,
      stepProgress: this.stepProgress,
      t: now,
    });
  }
}
